import { useEffect, useRef } from "react";
import { BOARDS_API_BASE_URL } from "../../constants/configuration";

// React hook that connects to /ws/board/:boardId on the boards service and
// fires `onEvent(event, payload)` for every server-pushed event.
// Auth: the cookie `urltoken` is sent automatically by the browser as long
// as the boards service hostname is same-site with the login system.
//
// Connection lifecycle:
//   open → mounts → emits hello → re-renders trigger nothing
//   server pushes events → onEvent fires
//   close (anything) → exponential backoff, up to 30s, until unmount
export default function useBoardWs(boardId, onEvent) {
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;

  useEffect(() => {
    if (!boardId) return undefined;
    let ws = null;
    let closed = false;
    let retry = 0;
    let timer = null;

    const base = BOARDS_API_BASE_URL.replace(/^http/, "ws").replace(/\/$/, "");
    const url = `${base}/ws/board/${boardId}`;

    const connect = () => {
      if (closed) return;
      try {
        ws = new WebSocket(url);
      } catch {
        scheduleRetry();
        return;
      }

      ws.onmessage = (e) => {
        let msg;
        try { msg = JSON.parse(e.data); } catch { return; }
        if (msg?.type === "event" && handlerRef.current) {
          handlerRef.current(msg.event, msg.payload, msg);
        }
      };

      ws.onopen = () => {
        retry = 0;
      };

      ws.onclose = () => {
        if (!closed) scheduleRetry();
      };

      ws.onerror = () => {
        // onclose will fire too; rely on that for retry.
      };
    };

    const scheduleRetry = () => {
      if (closed) return;
      retry += 1;
      const delay = Math.min(30000, 500 * 2 ** Math.min(retry, 6));
      timer = setTimeout(connect, delay);
    };

    connect();

    return () => {
      closed = true;
      if (timer) clearTimeout(timer);
      try { ws?.close(); } catch { /* ignore */ }
    };
  }, [boardId]);
}
